import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import * as SiIcons from 'react-icons/si'
import { useLanguage } from '../../contexts/LanguageContext'
import PetService from '../../services/petService'
import DisableShareConfirmModal from './DisableShareConfirmModal'

const {
  FiX, FiCopy, FiCheck, FiShare2, FiMail, FiExternalLink,
  FiInfo, FiEye, FiLock, FiSettings, FiCheckCircle,
  FiToggleLeft, FiToggleRight, FiImage, FiUserCheck,
  FiHeart, FiActivity, FiPhone, FiClipboard, FiFirstAid, FiAlertTriangle
} = FiIcons

const { SiWhatsapp, SiLine } = SiIcons

// ✅ 移到模組層級：單一實例，永不重建
const petService = new PetService()

// ✅ 移到模組層級：靜態顏色對應表，永不重建
const ACTIVE_COLOR_MAP = {
  emerald: 'bg-emerald-100 text-emerald-600',
  blue:    'bg-blue-100 text-blue-600',
  pink:    'bg-pink-100 text-pink-600',
  green:   'bg-green-100 text-green-600',
  red:     'bg-red-100 text-red-600',
  orange:  'bg-orange-100 text-orange-600',
  purple:  'bg-purple-100 text-purple-600',
}
const INACTIVE_COLOR = 'bg-gray-200 text-gray-500'

// ✅ 移到模組層級：每個選項的靜態設定（icon、color、key）
const SHARE_OPTION_KEYS = [
  { key: 'photo',           icon: FiImage,     color: 'emerald' },
  { key: 'basicInfo',       icon: FiUserCheck, color: 'blue'    },
  { key: 'dietaryInfo',     icon: FiHeart,     color: 'pink'    },
  { key: 'behaviorInfo',    icon: FiActivity,  color: 'green'   },
  { key: 'healthInfo',      icon: FiFirstAid,  color: 'red'     },
  { key: 'emergencyContact',icon: FiPhone,     color: 'orange'  },
  { key: 'specialNotes',    icon: FiClipboard, color: 'purple'  },
]

const DEFAULT_SHARE_OPTIONS = {
  basicInfo: true, dietaryInfo: true, behaviorInfo: true,
  healthInfo: true, emergencyContact: true, specialNotes: true, photo: true,
}

const ShareModal = ({ isOpen, onClose, pet, onPetUpdate }) => {
  const { t, currentLanguage } = useLanguage()
  const [copied, setCopied] = useState(false)
  const [activeTab, setActiveTab] = useState('customize')
  const [isGeneratingLink, setIsGeneratingLink] = useState(false)
  const [isEnablingShare, setIsEnablingShare] = useState(false)
  const [petData, setPetData] = useState(pet)
  const [showDisableConfirm, setShowDisableConfirm] = useState(false)
  const [isSavingPermissions, setIsSavingPermissions] = useState(false)
  const [shareOptions, setShareOptions] = useState(DEFAULT_SHARE_OPTIONS)

  useEffect(() => {
    if (!pet) return
    setPetData(pet)
    setShareOptions(
      pet.share_permissions && typeof pet.share_permissions === 'object'
        ? pet.share_permissions
        : DEFAULT_SHARE_OPTIONS
    )
  }, [pet])

  // ✅ 合併 body scroll lock 邏輯
  useEffect(() => {
    if (!isOpen) return
    const scrollY = window.scrollY
    const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth
    const prev = {
      overflow: document.body.style.overflow,
      paddingRight: document.body.style.paddingRight,
      position: document.body.style.position,
      top: document.body.style.top,
    }
    Object.assign(document.body.style, {
      overflow: 'hidden', position: 'fixed',
      top: `-${scrollY}px`, left: '0', right: '0',
      width: '100%', height: '100vh',
      paddingRight: `${scrollBarWidth}px`,
    })
    const preventTouchMove = (e) => {
      if (!e.target.closest('.modal-content')) e.preventDefault()
    }
    document.addEventListener('touchmove', preventTouchMove, { passive: false })
    return () => {
      Object.assign(document.body.style, {
        overflow: prev.overflow, paddingRight: prev.paddingRight,
        position: prev.position, top: prev.top,
        left: '', right: '', width: '', height: '',
      })
      if (prev.position !== 'fixed') window.scrollTo(0, scrollY)
      document.removeEventListener('touchmove', preventTouchMove)
    }
  }, [isOpen])

  // ✅ useMemo 快取 shareUrl，只在 shareOptions/token/language 變化時重算
  const shareUrl = useMemo(() => {
    if (!petData?.share_token || !petData?.is_shared) return ''
    const params = new URLSearchParams()
    Object.entries(shareOptions).forEach(([k, v]) => params.append(k, v ? 'true' : 'false'))
    return `${window.location.origin}/#/${currentLanguage}/share/${encodeURIComponent(petData.share_token)}?${params.toString()}`
  }, [petData?.share_token, petData?.is_shared, shareOptions, currentLanguage])

  // ✅ useMemo 快取 shareOptionItems（依賴 t，語言切換才重建）
  const shareOptionItems = useMemo(() => SHARE_OPTION_KEYS.map(item => ({
    ...item,
    title: t(`modals.share${item.key.charAt(0).toUpperCase() + item.key.slice(1)}`),
    description: t(`modals.share${item.key.charAt(0).toUpperCase() + item.key.slice(1)}Desc`),
  })), [t])

  const selectedCount = useMemo(
    () => Object.values(shareOptions).filter(Boolean).length,
    [shareOptions]
  )

  // ✅ 統一的權限更新函數，避免重複邏輯
  const applyPermissions = useCallback(async (newOptions) => {
    setShareOptions(newOptions)
    setIsSavingPermissions(true)
    try {
      const updatedPet = await petService.updateSharePermissions(petData.id, newOptions)
      setPetData(updatedPet)
      onPetUpdate?.(updatedPet)
    } catch {
      setShareOptions(shareOptions) // rollback
    } finally {
      setIsSavingPermissions(false)
    }
  }, [petData?.id, shareOptions, onPetUpdate])

  const toggleShareOption = useCallback((option) => {
    applyPermissions({ ...shareOptions, [option]: !shareOptions[option] })
  }, [shareOptions, applyPermissions])

  const handleSelectAll = useCallback(() => {
    applyPermissions(Object.keys(shareOptions).reduce((acc, k) => ({ ...acc, [k]: true }), {}))
  }, [shareOptions, applyPermissions])

  const handleDeselectAll = useCallback(() => {
    applyPermissions(Object.keys(shareOptions).reduce((acc, k) => ({ ...acc, [k]: false }), {}))
  }, [shareOptions, applyPermissions])

  const enableSharing = useCallback(async () => {
    setIsEnablingShare(true)
    try {
      const updatedPet = await petService.togglePetSharing(petData.id, true)
      setPetData(updatedPet)
      onPetUpdate?.(updatedPet)
    } catch (error) {
      console.error('啟用分享功能失敗:', error)
      alert(t('modals.shareEnableError') || '啟用分享功能失敗，請稍後再試')
    } finally {
      setIsEnablingShare(false)
    }
  }, [petData?.id, t, onPetUpdate])

  const copyToClipboard = useCallback(async () => {
    setIsGeneratingLink(true)
    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 3000)
    } catch {
      const ta = document.createElement('textarea')
      ta.value = shareUrl
      Object.assign(ta.style, { position: 'fixed', left: '-999999px', top: '-999999px' })
      document.body.appendChild(ta)
      ta.focus(); ta.select()
      try { document.execCommand('copy'); setCopied(true); setTimeout(() => setCopied(false), 3000) } catch {}
      document.body.removeChild(ta)
    }
    setTimeout(() => setIsGeneratingLink(false), 500)
  }, [shareUrl])

  const shareViaEmail = useCallback(() => {
    const subject = encodeURIComponent(t('modals.emailSubject', { petName: petData.name }))
    const body = encodeURIComponent(t('modals.emailBody', { petName: petData.name, shareUrl }))
    window.open(`mailto:?subject=${subject}&body=${body}`)
  }, [t, petData?.name, shareUrl])

  const shareViaWhatsApp = useCallback(() => {
    const msg = encodeURIComponent(t('modals.whatsappMessage', { petName: petData.name, shareUrl }))
    window.open(`https://wa.me/?text=${msg}`, '_blank', 'noopener,noreferrer')
  }, [t, petData?.name, shareUrl])

  const shareViaLine = useCallback(() => {
    const msg = encodeURIComponent(t('modals.lineMessage', { petName: petData.name }))
    const url = encodeURIComponent(shareUrl)
    window.open(`https://social-plugins.line.me/lineit/share?url=${url}&text=${msg}`, '_blank', 'noopener,noreferrer')
  }, [t, petData?.name, shareUrl])

  const previewShare = useCallback(() => {
    window.open(shareUrl, '_blank', 'noopener,noreferrer')
  }, [shareUrl])

  const handleDisableClick = useCallback(() => setShowDisableConfirm(true), [])

  const handleConfirmDisable = useCallback(async () => {
    setIsEnablingShare(true)
    try {
      const updatedPet = await petService.togglePetSharing(petData.id, false)
      setPetData(updatedPet)
      onPetUpdate?.(updatedPet)
      setShowDisableConfirm(false)
      onClose()
    } catch (error) {
      console.error('停用分享功能失敗:', error)
      alert(t('modals.shareDisableError') || '停用分享功能失敗，請稍後再試')
    } finally {
      setIsEnablingShare(false)
    }
  }, [petData?.id, t, onPetUpdate, onClose])

  if (!petData) return null

  // ✅ 尚未啟用分享時顯示啟用畫面
  if (!petData.share_token || !petData.is_shared) {
    return (
      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-[9999] overflow-hidden">
            <div className="flex items-center justify-center min-h-screen p-4">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/80 backdrop-blur-md" onClick={onClose} />
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                className="relative bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-8 max-w-md w-full">
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-xl transition-colors">
                  <SafeIcon icon={FiX} className="h-5 w-5" />
                </button>
                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full mx-auto mb-6 flex items-center justify-center">
                    <SafeIcon icon={FiShare2} className="w-10 h-10 text-blue-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t('modals.enableSharingFeature')}</h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-8 leading-relaxed">
                    {t('modals.sharingPrivacyNotice', { petName: petData.name })}
                  </p>
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                    <div className="flex items-start space-x-3">
                      <SafeIcon icon={FiInfo} className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div className="text-left">
                        <h4 className="font-semibold text-amber-800 text-sm mb-1">{t('modals.sharingFeatures')}</h4>
                        <ul className="text-amber-700 text-xs space-y-1">
                          <li>• {t('modals.uniqueSecureLink')}</li>
                          <li>• {t('modals.selectableInfoTypes')}</li>
                          <li>• {t('modals.disableAnytime')}</li>
                          <li>• {t('modals.noPersonalInfoLeak')}</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  <div className="flex space-x-3">
                    <button onClick={onClose} className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-semibold">
                      {t('modals.notNow')}
                    </button>
                    <button onClick={enableSharing} disabled={isEnablingShare}
                      className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all font-semibold disabled:opacity-50 flex items-center justify-center space-x-2">
                      {isEnablingShare ? (
                        <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /><span>{t('modals.enabling')}</span></>
                      ) : (
                        <><SafeIcon icon={FiShare2} className="h-4 w-4" /><span>{t('modals.enableSharing')}</span></>
                      )}
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>
    )
  }

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-[9999] overflow-hidden">
            <div className="flex items-center justify-center min-h-screen p-4">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/80 backdrop-blur-md transition-opacity"
                onClick={onClose} style={{ touchAction: 'none' }} />
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="modal-content relative bg-white dark:bg-gray-800 rounded-3xl shadow-2xl border border-gray-200 dark:border-gray-700 max-w-2xl w-full max-h-[95vh] overflow-hidden"
                onClick={(e) => e.stopPropagation()} style={{ touchAction: 'auto' }}
              >
                {/* Header */}
                <div className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 px-8 py-6 text-white sticky top-0 z-20 border-b border-white/10">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center min-w-0 flex-1">
                      <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mr-4 flex-shrink-0">
                        <SafeIcon icon={FiShare2} className="text-3xl text-white" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-2xl font-bold text-white leading-tight truncate">
                          {t('modals.shareModalTitle', { petName: petData.name })}
                        </h3>
                        <p className="text-white/90 mt-1 text-sm">{t('modals.shareModalSubtitle')}</p>
                      </div>
                    </div>
                    <button onClick={onClose} className="text-white/80 hover:text-white p-2 hover:bg-white/10 rounded-xl transition-colors flex-shrink-0 ml-4">
                      <SafeIcon icon={FiX} className="h-6 w-6" />
                    </button>
                  </div>
                </div>

                <div className="overflow-y-auto max-h-[calc(95vh-120px)] overscroll-contain">
                  <div className="px-8 py-6">
                    {/* Tabs */}
                    <div className="flex space-x-2 bg-gray-100 dark:bg-gray-700 rounded-2xl p-2 mb-8">
                      {[
                        { id: 'customize', icon: FiSettings, label: t('modals.shareSelectInfo') },
                        { id: 'share',     icon: FiShare2,   label: t('modals.shareVia') },
                      ].map(tab => (
                        <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                          className={`flex-1 py-4 px-6 rounded-xl text-sm font-semibold transition-all duration-300 flex items-center justify-center space-x-2 ${
                            activeTab === tab.id
                              ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-lg scale-[1.02] ring-2 ring-indigo-500/20'
                              : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 hover:bg-gray-50 dark:hover:bg-gray-600/50'
                          }`}>
                          <SafeIcon icon={tab.icon} className="h-4 w-4" />
                          <span>{tab.label}</span>
                        </button>
                      ))}
                    </div>

                    <div className="min-h-[400px]">
                      {/* Customize Tab */}
                      {activeTab === 'customize' && (
                        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }} className="space-y-6">
                          <div className="text-center mb-8">
                            <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-3">{t('modals.shareSelectInfo')}</h4>
                            <div className="inline-flex items-center px-4 py-2 bg-indigo-50 dark:bg-indigo-900/30 rounded-full border border-indigo-200">
                              <SafeIcon icon={FiCheckCircle} className="h-4 w-4 text-indigo-600 mr-2" />
                              <span className="text-sm font-semibold text-indigo-700 dark:text-indigo-300">
                                {t('modals.selectedItems', { count: selectedCount })}
                              </span>
                            </div>
                            {isSavingPermissions && (
                              <div className="mt-2 text-xs text-gray-500 flex items-center justify-center space-x-2">
                                <div className="animate-spin rounded-full h-3 w-3 border-2 border-indigo-500 border-t-transparent" />
                                <span>儲存中...</span>
                              </div>
                            )}
                          </div>

                          <div className="space-y-4">
                            {shareOptionItems.map((item, index) => {
                              const isActive = shareOptions[item.key]
                              return (
                                <motion.div key={item.key} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}
                                  className={`relative p-5 rounded-2xl border-2 cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-[1.02] ${
                                    isActive
                                      ? 'border-indigo-200 bg-indigo-50 dark:bg-indigo-900/20 dark:border-indigo-700 shadow-md'
                                      : 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 hover:border-gray-300 hover:bg-gray-100'
                                  }`}
                                  onClick={() => toggleShareOption(item.key)}>
                                  <div className="flex items-center space-x-4">
                                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm transition-colors duration-300 ${
                                      isActive ? ACTIVE_COLOR_MAP[item.color] : INACTIVE_COLOR
                                    }`}>
                                      <SafeIcon icon={item.icon} className="h-7 w-7" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center justify-between mb-1">
                                        <h5 className={`font-bold text-lg transition-colors duration-300 ${isActive ? 'text-indigo-900 dark:text-indigo-100' : 'text-gray-900 dark:text-gray-100'}`}>
                                          {item.title}
                                        </h5>
                                        <SafeIcon icon={isActive ? FiToggleRight : FiToggleLeft}
                                          className={`h-8 w-8 transition-colors duration-300 ${isActive ? 'text-indigo-600' : 'text-gray-400'}`} />
                                      </div>
                                      <p className={`text-sm leading-relaxed transition-colors duration-300 ${isActive ? 'text-indigo-700 dark:text-indigo-300' : 'text-gray-600 dark:text-gray-400'}`}>
                                        {item.description}
                                      </p>
                                    </div>
                                  </div>
                                </motion.div>
                              )
                            })}
                          </div>

                          <div className="flex space-x-4 pt-6 border-t border-gray-200 dark:border-gray-600">
                            <button onClick={handleSelectAll} disabled={isSavingPermissions}
                              className="flex-1 py-3 px-6 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl hover:from-indigo-600 hover:to-purple-700 transition-all text-sm font-semibold shadow-lg hover:scale-105 disabled:opacity-50">
                              {t('modals.shareSelectAll')}
                            </button>
                            <button onClick={handleDeselectAll} disabled={isSavingPermissions}
                              className="flex-1 py-3 px-6 bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-200 transition-all text-sm font-semibold hover:scale-105 disabled:opacity-50">
                              {t('modals.shareDeselectAll')}
                            </button>
                          </div>
                        </motion.div>
                      )}

                      {/* Share Tab */}
                      {activeTab === 'share' && (
                        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }} className="space-y-8">
                          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-2xl p-6 border border-indigo-200">
                            <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
                              <SafeIcon icon={FiEye} className="h-5 w-5 text-indigo-600" />
                              <span>{t('modals.preview')}</span>
                            </h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">{t('modals.previewDescription')}</p>
                            <button onClick={previewShare}
                              className="w-full px-6 py-4 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white rounded-2xl transition-all hover:scale-[1.02] flex items-center justify-center space-x-3 group shadow-lg font-semibold">
                              <SafeIcon icon={FiEye} className="h-6 w-6" />
                              <span>{t('modals.preview')}</span>
                              <SafeIcon icon={FiExternalLink} className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                            </button>
                          </div>

                          <div className="space-y-4">
                            <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-6 flex items-center space-x-2">
                              <SafeIcon icon={FiShare2} className="h-5 w-5 text-gray-600" />
                              <span>{t('modals.chooseShareMethod')}</span>
                            </h4>
                            {[
                              { fn: shareViaEmail, bg: 'from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 border-blue-200', iconBg: 'bg-blue-200 group-hover:bg-blue-300', text: 'text-blue-700', icon: FiMail, title: t('modals.shareViaEmail'), desc: t('modals.shareViaEmailDesc'), iconColor: 'text-blue-500' },
                              { fn: shareViaWhatsApp, bg: 'from-green-50 to-green-100 hover:from-green-100 hover:to-green-200 border-green-200', iconBg: 'bg-green-200 group-hover:bg-green-300', text: 'text-green-700', icon: SiWhatsapp, title: t('modals.shareViaWhatsApp'), desc: t('modals.shareViaWhatsAppDesc'), iconColor: 'text-green-500' },
                              { fn: shareViaLine, bg: 'from-emerald-50 to-teal-100 hover:from-emerald-100 hover:to-teal-200 border-emerald-200', iconBg: 'bg-emerald-200 group-hover:bg-emerald-300', text: 'text-emerald-700', icon: SiLine, title: t('modals.shareViaLine'), desc: t('modals.shareViaLineDesc'), iconColor: 'text-emerald-500' },
                            ].map((btn, i) => (
                              <button key={i} onClick={btn.fn}
                                className={`w-full px-6 py-5 bg-gradient-to-r ${btn.bg} ${btn.text} rounded-2xl transition-all duration-300 hover:scale-[1.02] flex items-center space-x-4 group shadow-md hover:shadow-lg border`}>
                                <div className={`w-14 h-14 ${btn.iconBg} rounded-2xl flex items-center justify-center transition-colors duration-300 shadow-sm`}>
                                  <SafeIcon icon={btn.icon} className="h-7 w-7" />
                                </div>
                                <div className="text-left flex-1 min-w-0">
                                  <div className="font-bold text-lg">{btn.title}</div>
                                  <div className="text-sm opacity-80">{btn.desc}</div>
                                </div>
                                <SafeIcon icon={FiExternalLink} className={`h-5 w-5 ${btn.iconColor} group-hover:translate-x-1 transition-transform flex-shrink-0`} />
                              </button>
                            ))}
                          </div>

                          <div className="pt-4">
                            <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-600 rounded-2xl p-6 border border-gray-200 dark:border-gray-600">
                              <label className="block text-base font-bold text-gray-700 dark:text-gray-300 mb-4 flex items-center space-x-2">
                                <SafeIcon icon={FiShare2} className="h-5 w-5 text-gray-600" />
                                <span>{t('modals.shareableLink')}</span>
                              </label>
                              <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-3">
                                <input type="text" value={shareUrl} readOnly
                                  className="flex-1 px-4 py-4 border border-gray-300 dark:border-gray-500 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500 select-all"
                                  onClick={(e) => e.target.select()} />
                                <button onClick={copyToClipboard} disabled={isGeneratingLink}
                                  className={`px-6 py-4 rounded-xl transition-all duration-300 flex items-center justify-center space-x-2 font-semibold shadow-lg hover:shadow-xl w-full sm:w-auto sm:min-w-[140px] ${
                                    copied ? 'bg-green-600 hover:bg-green-700 text-white scale-105'
                                    : isGeneratingLink ? 'bg-gray-400 text-white cursor-not-allowed'
                                    : 'bg-indigo-600 hover:bg-indigo-700 text-white hover:scale-105'
                                  }`}>
                                  {isGeneratingLink
                                    ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                                    : <SafeIcon icon={copied ? FiCheck : FiCopy} className="h-4 w-4" />}
                                  <span className="text-sm">
                                    {copied ? t('modals.copied') : isGeneratingLink ? t('modals.generating') : t('modals.copyLink')}
                                  </span>
                                </button>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </div>

                    {/* Security notice */}
                    <div className="mt-8 p-6 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl">
                      <div className="flex items-start space-x-4">
                        <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <SafeIcon icon={FiLock} className="text-amber-600 h-5 w-5" />
                        </div>
                        <div className="flex-1">
                          <h4 className="text-base font-bold text-amber-800 mb-2">{t('modals.securityReminder')}</h4>
                          <p className="text-sm text-amber-700 leading-relaxed">{t('modals.shareOnlyWithTrusted')}</p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-8 flex justify-between items-center">
                      <button onClick={handleDisableClick}
                        className="px-6 py-3 bg-red-100 text-red-700 rounded-xl hover:bg-red-200 transition-all font-semibold hover:scale-105 shadow-sm flex items-center space-x-2">
                        <SafeIcon icon={FiAlertTriangle} className="h-4 w-4" />
                        <span>{t('modals.disableSharing')}</span>
                      </button>
                      <button onClick={onClose}
                        className="px-8 py-3 bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-200 transition-all font-semibold hover:scale-105 shadow-sm">
                        {t('modals.close')}
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>

      <DisableShareConfirmModal
        isOpen={showDisableConfirm}
        onClose={() => setShowDisableConfirm(false)}
        onConfirm={handleConfirmDisable}
        isProcessing={isEnablingShare}
      />
    </>
  )
}

export default ShareModal